// TemplateValidator.jsx - version avec validation de la CONFIG + regroupements
import React, { useEffect, useState } from "react";
import PizZip from "pizzip";
import * as XLSX from "xlsx";

/* =========================
   Utils & helpers
========================= */
const isDocx = (n="") => n.toLowerCase().endsWith(".docx");
const isXlsx = (n="") => n.toLowerCase().endsWith(".xlsx");
const isCsv  = (n="") => n.toLowerCase().endsWith(".csv");
const isJson = (n="") => n.toLowerCase().endsWith(".json");

const uniq = (arr) => Array.from(new Set(arr));
const trimBraces = (s) => s?.replace(/^\{+|\}+$/g, "").trim();
const escapeRegExp = (s="") => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const ALLOWED_TYPES = new Set(["text","textarea","select","checkbox","number","date","image","array"]);

function classifyKeyName(k=""){
  return (k||"").replace(/\s+/g,"_").replace(/[^a-zA-Z0-9_#./%]/g,"").toLowerCase();
}
function safeBool(v){
  const s = String(v ?? "").trim().toLowerCase();
  return ["true","1","oui","yes"].includes(s);
}
function tryRegex(rxStr){
  try { if (!rxStr) return null; return new RegExp(rxStr); } catch { return "INVALID"; }
}
// Vérifie un mask de type react-input-mask : 
// 9 = chiffre, a = lettre, * = alphanumérique ; autres caractères = littéraux
function validateMaskPattern(maskStr = "") {
  const s = String(maskStr);
  if (!s) return { ok: true };
  // autoriser uniquement: 9, a, *, et les littéraux . , - / ( ) espace
  // (tu peux étendre la liste des littéraux si besoin)
  const allowedLiteral = /[0-9a-zA-Z*#]/; // on check token par token plus bas
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    // placeholders autorisés
    if (ch === "9" || ch === "a" || ch === "*") continue;
    // littéraux autorisés (tu peux en ajouter : '_', ':', etc.)
    if (" .,-/()".includes(ch)) continue;
    // sinon c'est inconnu
    if (!allowedLiteral.test(ch) && !(" .,-/()".includes(ch))) {
      return { ok: false, reason: `caractère inattendu '${ch}'` };
    }
  }
  return { ok: true };
}

function placeholdersIn(str=""){
  // {{key}} extraction pour outputNameTemplate (JSON)
  const out = [];
  const re = /\{\{([^{}]+)\}\}/g;
  let m;
  while ((m = re.exec(str))) {
    out.push(m[1].trim());
  }
  return uniq(out);
}

/* =========================
   DOCX parsing
========================= */
async function extractDocxXmlStrings(file){
  const ab = await file.arrayBuffer();
  const zip = new PizZip(ab);
  const paths = [
    "word/document.xml",
    "word/header1.xml","word/header2.xml","word/header3.xml",
    "word/footer1.xml","word/footer2.xml","word/footer3.xml",
  ];
  const xmls = [];
  for (const p of paths) {
    const f = zip.file(p);
    if (f) xmls.push({ path: p, xml: f.asText() });
  }
  if (!xmls.length) throw new Error("Fichier .docx invalide: document.xml introuvable.");
  return xmls;
}
const stripXmlToText = (xml) =>
  xml.replace(/<w:.*?>/g,"").replace(/<\/w:.*?>/g,"").replace(/<[^>]+>/g,"");

function extractTagsFromXml(xml){
  const text = stripXmlToText(xml);
  const tagRegex = /\{[^{}\n\r]+\}/g;
  const matches = text.match(tagRegex) || [];
  return { text, tags: matches.map((m)=>m.trim()) };
}

function normalizeTagInner(innerRaw){
  const innerTrim = innerRaw.trim();
  const isSectionOpen  = innerTrim.startsWith("#");
  const isSectionClose = innerTrim.startsWith("/");
  const isImage        = innerTrim.startsWith("%");
  let keyPart = innerTrim.replace(/^[#/%]/,"");
  const keyTrim = keyPart.trim();
  return {
    innerRaw, innerTrim, isSectionOpen, isSectionClose, isImage, keyTrim,
    hasLeadingOrTrailingWS: innerRaw !== innerTrim,
    hasInnerWS: /\s/.test(keyPart),
  };
}

async function parseDocxTags(file){
  if (!file || !isDocx(file.name||"")) throw new Error("Sélectionne un fichier .docx valide");
  const xmls = await extractDocxXmlStrings(file);
  const extracted = xmls.map(({xml})=>extractTagsFromXml(xml));
  const fullText = extracted.map((e)=>e.text).join("\n");
  const tags = extracted.flatMap((e)=>e.tags); // ex: "{ nom }", "{%logo}", "{# lignes}"

  const badWhitespaceEntries = [];
  const cleaned = [];

  for (const raw of tags) {
    const innerRaw = raw.slice(1,-1);
    const norm = normalizeTagInner(innerRaw);

    if (norm.isSectionOpen || norm.isSectionClose) {
      cleaned.push(norm.innerTrim);
    } else {
      cleaned.push(norm.keyTrim.startsWith("%") ? norm.keyTrim.slice(1) : norm.keyTrim);
    }

    if (norm.hasLeadingOrTrailingWS || norm.hasInnerWS) {
      let sug = "{";
      if (norm.isSectionOpen)      sug += `#${norm.keyTrim}`;
      else if (norm.isSectionClose) sug += `/${norm.keyTrim}`;
      else if (norm.isImage)        sug += `%${norm.keyTrim}`;
      else                          sug += norm.keyTrim;
      sug += "}";
      badWhitespaceEntries.push({ raw, suggestion: sug });
    }
  }

  const cleanedUnique = uniq(cleaned.map(trimBraces).filter(Boolean));
  const sectionsOpen  = cleanedUnique.filter((k)=>k.startsWith("#"));
  const sectionsClose = cleanedUnique.filter((k)=>k.startsWith("/"));

  const percentTagKeys = uniq(
    tags
      .map((raw)=>raw.slice(1,-1).trim())
      .filter((inner)=>inner.startsWith("%"))
      .map((inner)=>inner.replace(/^%+/,"").trim())
  );

  const keys = uniq(
    cleanedUnique
      .filter((k)=>!k.startsWith("#") && !k.startsWith("/"))
      .map((k)=>k.replace(/^%+/,""))
  );

  return { keys, sectionsOpen, sectionsClose, percentTagKeys, fullText, badWhitespaceEntries };
}

function validateDocxSections(openArr, closeArr){
  const errors = [];
  const openNames  = openArr.map((s)=>s.slice(1));
  const closeNames = closeArr.map((s)=>s.slice(1));
  for (const n of openNames) {
    if (!closeNames.includes(n)) {
      errors.push({ code:"section_unclosed", level:"error", msg:`Section {#${n}} sans fermeture {/${n}}` });
    }
  }
  for (const n of closeNames) {
    if (!openNames.includes(n)) {
      errors.push({ code:"section_orphan_close", level:"error", msg:`Fermeture {/${n}} sans ouverture {#${n}}` });
    }
  }
  return { errors, openNames: uniq(openNames) };
}

/* =========================
   CONFIG parsing (Fields + Maps éventuel)
========================= */
async function parseConfigFile(file){
  if (!file) return { rows:[], maps: {}, format:null, jsonOutputNameTemplate: "" };
  const name = file.name || "";
  const ab = await file.arrayBuffer();

  if (isXlsx(name)) {
    const wb = XLSX.read(ab, { type:"array" });
    const sheetNames = wb.SheetNames || [];

    const fieldsSheetName =
      sheetNames.find(n=>n.toLowerCase()==="fields") ||
      sheetNames.find(n=>n.toLowerCase().includes("fields")) ||
      sheetNames[0];

    const sheet = wb.Sheets[fieldsSheetName];
    const rows  = XLSX.utils.sheet_to_json(sheet, { defval:"" });

    // Maps (facultatif)
    const mapsSheetName = sheetNames.find(n=>n.toLowerCase()==="maps");
    const maps = {};
    if (mapsSheetName) {
      const ms = wb.Sheets[mapsSheetName];
      const mrows = XLSX.utils.sheet_to_json(ms, { defval:"" });
      // on attend colonnes: map_key, map_label, map_value, is_default
      for (const r of mrows) {
        const mk = String(r.map_key ?? r.MapKey ?? r.MAP_KEY ?? "").trim();
        if (!mk) continue;
        if (!maps[mk]) maps[mk] = [];
        maps[mk].push({
          label: String(r.map_label ?? r.label ?? r.Label ?? "").trim(),
          value: String(r.map_value ?? r.value ?? r.Value ?? "").trim(),
          is_default: safeBool(r.is_default ?? r.default ?? r.Default)
        });
      }
    }
    return { rows, maps, format:"xlsx", jsonOutputNameTemplate: "" };
  }

  if (isCsv(name)) {
    const text = new TextDecoder().decode(ab);
    const rows = XLSX.utils.sheet_to_json(XLSX.utils.csv_to_sheet(text), { defval:"" });
    return { rows, maps:{}, format:"csv", jsonOutputNameTemplate: "" };
  }

  if (isJson(name)) {
    const text = new TextDecoder().decode(ab);
    const data = JSON.parse(text);

    let rows = [];
    if (Array.isArray(data)) rows = data;
    else if (Array.isArray(data?.Fields)) rows = data.Fields;
    else if (Array.isArray(data?.fields)) rows = data.fields;
    else if (Array.isArray(data?.outputNameTemplate?.Fields)) rows = data.outputNameTemplate.Fields;

    const mapsArr = Array.isArray(data?.Maps) ? data.Maps : [];
    const maps = {};
    for (const r of mapsArr) {
      const mk = String(r.map_key ?? r.MapKey ?? r.MAP_KEY ?? "").trim();
      if (!mk) continue;
      if (!maps[mk]) maps[mk] = [];
      maps[mk].push({
        label: String(r.map_label ?? r.label ?? r.Label ?? "").trim(),
        value: String(r.map_value ?? r.value ?? r.Value ?? "").trim(),
        is_default: safeBool(r.is_default ?? r.default ?? r.Default)
      });
    }

    const jsonOutputNameTemplate = String(data?.outputNameTemplate?.template || data?.outputNameTemplate || "");

    return { rows, maps, format:"json", jsonOutputNameTemplate };
  }

  throw new Error("Format de config non supporté (xlsx/csv/json)");
}

function indexConfigRows(rows){
  const keys=[]; const keyRows={};
  const sections=new Set(); const repeatSections=new Set();
  const required=new Set(); const regexByKey={}; const typeByKey={}; const groupByKey={};
  const optionsByKey={}; const useMapByKey={}; const maskByKey={}; const maxLenByKey={};

  for(const r of rows){
    const key=String(
      r.key ?? r.Key ?? r.KEY ??
      r.cle ?? r.clé ?? r.Cle ?? r.Clé ??
      r.name ?? r.Name ?? r.Nom ?? r.nom ?? ""
    ).trim();
    if(!key) continue;
    keys.push(key); keyRows[key]=r;

    const type=String(r.type ?? r.Type ?? r.TYPE ?? "").trim().toLowerCase();
    if(type) typeByKey[key]=type;

    const group=String(
      r.group_key ?? r.GroupKey ?? r.GROUP_KEY ??
      r.section ?? r.Section ?? ""
    ).trim();
    if(group){ sections.add(group); groupByKey[key]=group; }

    const repeat = safeBool(r.repeat_section ?? r.RepeatSection ?? r.REPEAT_SECTION);
    if (repeat) {
      const name = group || String(r.section ?? r.Section ?? "").trim();
      if (name) repeatSections.add(name);
    }

    const req = safeBool(r.required ?? r.Required ?? r.REQUIRED ?? r.obligatoire ?? r.Obligatoire);
    if (req) required.add(key);

    const rx = String(r.regex_validation ?? r.RegexValidation ?? r.REGEX_VALIDATION ?? r.regex ?? r.Regex ?? "");
    if (rx) regexByKey[key]=rx;

    // Options : accepter soit un tableau, soit une chaîne "A,B,C"
    let optionsRaw =
      r.options ?? r.Options ??
      r.valeurs ?? r.Valeurs ??
      r.values ?? r.Values;

    let parsedOptions = [];
    if (Array.isArray(optionsRaw)) {
      parsedOptions = optionsRaw.map(x => String(x)).map(s => s.trim()).filter(Boolean);
    } else if (typeof optionsRaw === "string" && optionsRaw.trim() !== "") {
      // Accepte virgules, points-virgules, retours ligne
      parsedOptions = optionsRaw
        .split(/[,\n;]+/g)
        .map(s => s.trim())
        .filter(Boolean);
    }
    // (optionnel) récupérer aussi options_1, options_2...
    Object.keys(r).forEach((kname) => {
      if (/^options?\s*_?\d+$/i.test(kname) && String(r[kname] ?? "").trim() !== "") {
        parsedOptions.push(String(r[kname]).trim());
      }
    });
    parsedOptions = Array.from(new Set(parsedOptions));
    if (parsedOptions.length > 0) {
      optionsByKey[key] = parsedOptions;
    }

    const use_map = r.use_map ?? r.UseMap ?? r.useMap;
    if (use_map) useMapByKey[key] = String(use_map).trim();

    const mask = r.mask ?? r.Mask;
    if (mask !== undefined) maskByKey[key] = String(mask);

    const maxlen = r.max_length ?? r.maxLength ?? r.MaxLength;
    if (maxlen !== undefined) maxLenByKey[key] = maxlen;
  }

  return {
    keys: uniq(keys),
    keyRows,
    sections: Array.from(sections),
    repeatSections: Array.from(repeatSections),
    required: Array.from(required),
    regexByKey,
    typeByKey,
    groupByKey,
    optionsByKey,
    useMapByKey,
    maskByKey,
    maxLenByKey,
  };
}

/* =========================
   CONFIG validation (structure/usage)
========================= */
function validateConfigStructure(cfgIdx, cfgRawMaps, jsonOutputNameTemplate){
  const issues = [];

  // 1) Fields vide
  if ((cfgIdx.keys||[]).length === 0) {
    issues.push({ code:"config_fields_empty", level:"error", msg:"La config ne contient aucune ligne dans 'Fields'." });
    return issues;
  }

  // 2) Doublons & clés vides (on a déjà filtré les vides au parsing, mais on signale si on en a ignoré)
  const dupMap = {};
  for (const k of cfgIdx.keys) dupMap[k] = (dupMap[k]||0)+1;
  const dups = Object.keys(dupMap).filter(k=>dupMap[k]>1);
  if (dups.length>0) {
    issues.push({ code:"config_duplicate_keys", level:"error", msg:`Clés dupliquées dans 'Fields': ${dups.join(", ")}` });
  }

  // 3) Types
  for (const [k,t] of Object.entries(cfgIdx.typeByKey||{})) {
    if (t && !ALLOWED_TYPES.has(t)) {
      issues.push({ code:"type_unknown", level:"error", msg:`Type inconnu pour "${k}": ${t} (autorisés: ${Array.from(ALLOWED_TYPES).join(", ")})` });
    }
  }

  // 4) Select/options
  for (const k of cfgIdx.keys) {
    const t = (cfgIdx.typeByKey||{})[k];
    if (t === "select") {
      const hasOptions = Array.isArray((cfgIdx.optionsByKey||{})[k]) && cfgIdx.optionsByKey[k].length>0;
      const useMap = (cfgIdx.useMapByKey||{})[k];
      if (!hasOptions && !useMap) {
        issues.push({ code:"select_without_options", level:"warn", msg:`Champ "${k}" de type select sans options ni use_map.` });
      }
      if (useMap) {
        const map = cfgRawMaps[useMap];
        if (!map || !Array.isArray(map) || map.length===0) {
          issues.push({ code:"map_missing", level:"error", msg:`use_map="${useMap}" introuvable pour le champ "${k}".` });
        } else {
          const badEntries = map.filter(e=>!e || !String(e.label||"").trim() || !String(e.value||"").trim());
          if (badEntries.length>0) {
            issues.push({ code:"map_invalid_entries", level:"warn", msg:`Map "${useMap}" a des entrées sans label ou value.` });
          }
          const defaults = map.filter(e=>e.is_default);
          if (defaults.length>1) {
            issues.push({ code:"map_multiple_defaults", level:"warn", msg:`Map "${useMap}" a plusieurs valeurs 'default'. La première sera utilisée.` });
          }
        }
      }
    }
  }

  // 5) Regex/mask/max_length
  for (const [k,rxStr] of Object.entries(cfgIdx.regexByKey||{})) {
    const res = tryRegex(rxStr);
    if (res === "INVALID") {
      issues.push({ code:"regex_invalid", level:"warn", msg:`regex_validation invalide pour "${k}".` });
    }
  }
  // Mask : vide accepté ; si non vide => valider la forme
  for (const [k,mask] of Object.entries(cfgIdx.maskByKey||{})) {
    const m = String(mask ?? "");
    if (m.trim() === "") continue; // [ok] on ne signale plus les masks vides
    const res = validateMaskPattern(m);
    if (!res.ok) {
      issues.push({ code:"mask_invalid", level:"warn", msg:`Mask invalide pour "${k}" (${res.reason}).` });
    }
  }
  for (const [k,ml] of Object.entries(cfgIdx.maxLenByKey||{})) {
    if (ml!==undefined && ml!=="" && isNaN(Number(ml))) {
      issues.push({ code:"max_length_nan", level:"warn", msg:`max_length non numérique pour "${k}": ${ml}` });
    }
  }

  // 6) outputNameTemplate (JSON uniquement)
  if (jsonOutputNameTemplate) {
    const ph = placeholdersIn(jsonOutputNameTemplate);
    const unknown = ph.filter(p => !cfgIdx.keys.includes(p));
    if (unknown.length>0) {
      issues.push({ code:"output_name_unknown_keys", level:"warn", msg:`outputNameTemplate utilise des clés inconnues: ${unknown.join(", ")}` });
    }
  }

  return issues;
}

/* =========================
   DOCX <-> CONFIG comparaison
========================= */
function compareDocxAndConfig(docx, cfg, hasConfig){
  const issues=[];
  const docxKeys=new Set(docx.keys);
  const cfgKeys=new Set(cfg.keys);

  // (A) erreurs de forme dans DOCX
  for (const entry of docx.badWhitespaceEntries || []) {
    issues.push({ code:"whitespace_in_placeholder", level:"error", msg:`Espace(s) dans la balise ${entry.raw} -> utilise ${entry.suggestion}` });
  }

  // (B) images: type=image => DOCX doit utiliser {%key}
  if (hasConfig && cfg.typeByKey) {
    for (const [k,t] of Object.entries(cfg.typeByKey)) {
      if (t==="image") {
        const hasPercent = docx.percentTagKeys?.includes(k);
        const appearsAsTextTag = docxKeys.has(k); // {logo} normalisé -> "logo"
        if (!hasPercent && appearsAsTextTag) {
          issues.push({ code:"image_placeholder_should_have_percent", level:"error", msg:`Le champ image "${k}" doit être utilisé comme {%${k}} dans le DOCX.` });
        }
      }
    }
  }

  // (C) matching clés
  const unusedKeys = []; // on regroupe les "unused"
  if (hasConfig) {
    if ((cfg.keys||[]).length===0) {
      issues.push({ code:"config_fields_empty", level:"warn", msg:"Config fournie mais aucun champ trouvé dans 'Fields'." });
    } else {
      // missing (DOCX -> config)
      for (const k of docxKeys) {
        if (k.startsWith("#")||k.startsWith("/")) continue;
        if (!cfgKeys.has(k)) {
          const imageSibling = Object.entries(cfg.typeByKey||{}).find(([ck,tp])=>tp==="image" && classifyKeyName(ck)===classifyKeyName(k));
          const isImageLike = !!imageSibling;
          const kClass = classifyKeyName(k);
          const suggestion = cfg.keys.find((x)=>classifyKeyName(x)===kClass);
          const hint = suggestion && suggestion!==k ? ` (voulais-tu "${suggestion}" ?)` : "";
          issues.push({ code:"missing_key", level: isImageLike ? "warn" : "error", msg:`Clé présente dans le DOCX mais absente de la config: {${k}}${hint}` });
        }
      }
      // unused (config -> DOCX) + "{key" sans "}"
      for (const k of cfg.keys) {
        if (!docxKeys.has(k)) {
          let malformed=false;
          if (docx.fullText) {
            const rxStart=new RegExp(`\\{\\s*${escapeRegExp(k)}(?![^}]*\\})`);
            const rxStartImg=new RegExp(`\\{\\s*%\\s*${escapeRegExp(k)}(?![^}]*\\})`);
            if (rxStart.test(docx.fullText) || rxStartImg.test(docx.fullText)) {
              issues.push({ code:"malformed_placeholder", level:"error", msg:`Balise mal formée pour la clé "${k}" (accolade fermante manquante ?).` });
              malformed=true;
            }
          }
          if (!malformed) unusedKeys.push(k);
        }
      }
      if (unusedKeys.length>0) {
        const label = unusedKeys.length>1 ? "Clés définies" : "Clé définie";
        const plural = unusedKeys.length>1 ? "non utilisées" : "non utilisée";
        issues.push({ code:"unused_key", level:"warn", msg:`${label} dans la config mais ${plural} dans le DOCX: ${unusedKeys.join(", ")}` });
      }
    }
  }

  // (D) sections
  const sect = validateDocxSections(docx.sectionsOpen, docx.sectionsClose);
  issues.push(...sect.errors);

  if (hasConfig && (cfg.keys||[]).length>0) {
    for (const s of (cfg.repeatSections||[])) {
      if (!sect.openNames.includes(s)) {
        issues.push({ code:"repeat_section_missing_in_docx", level:"error", msg:`repeat_section=true pour "${s}" mais le DOCX n'a pas {#${s}}...{/${s}}.` });
      }
    }
    for (const s of sect.openNames) {
      const hasAny = Object.values(cfg.groupByKey||{}).some(g=>g===s) || (cfg.repeatSections||[]).includes(s);
      if (!hasAny) {
        issues.push({ code:"section_no_fields", level:"warn", msg:`Le DOCX a la section "${s}" mais aucun champ de config n'est associé (group_key ou repeat_section).` });
      }
    }
  }

  const errors=issues.filter(i=>i.level==="error");
  const warnings=issues.filter(i=>i.level==="warn");
  return { errors, warnings, info:[], sections: sect.openNames };
}

/* =========================
   Public API
========================= */
export async function validateTemplateAndConfig(templateFile, configFile){
  if (!templateFile) {
    return { ok:false, errors:[], warnings:[], info:[], docx:null, config:null, hasConfig:!!configFile };
  }
  const docx = await parseDocxTags(templateFile);
  const hasConfig = !!configFile;

  const cfgRaw = hasConfig
    ? await parseConfigFile(configFile).catch(()=>({ rows:[], maps:{}, format:null, jsonOutputNameTemplate:"" }))
    : { rows:[], maps:{}, format:null, jsonOutputNameTemplate:"" };

  const cfgIdx = indexConfigRows(cfgRaw.rows||[]);
  // 1) Validation de structure/usage de la CONFIG
  const cfgIssues = validateConfigStructure(cfgIdx, cfgRaw.maps||{}, cfgRaw.jsonOutputNameTemplate || "");
  const cfgErrors = cfgIssues.filter(i=>i.level==="error");
  const cfgWarnings = cfgIssues.filter(i=>i.level==="warn");

  // 2) Comparaison DOCX <-> CONFIG
  const comp = compareDocxAndConfig(docx, cfgIdx, hasConfig);
  const ok = (cfgErrors.length + comp.errors.length) === 0;

  return {
    ok,
    errors: [...cfgErrors, ...comp.errors],
    warnings: [...cfgWarnings, ...comp.warnings],
    info: comp.info,
    docx: {
      keys: docx.keys,
      sectionsOpen: docx.sectionsOpen,
      sectionsClose: docx.sectionsClose,
      percentTagKeys: docx.percentTagKeys,
    },
    config: {
      keys: cfgIdx.keys,
      sections: cfgIdx.sections,
      repeatSections: cfgIdx.repeatSections,
    },
    hasConfig,
  };
}

/* =========================
   React component (UI SANS résumé)
========================= */
export default function TemplateValidator({ templateFile, configFile, onValidationChange }){
  const [state,setState]=useState({loading:false,result:null,err:null});

  useEffect(()=>{
    let cancelled=false;

    if (!templateFile) {
      setState({ loading:false, result:null, err:null });
      onValidationChange && onValidationChange({ ok:false, errors:[], warnings:[], info:[] });
      return;
    }

    // reset immédiat pour effacer les anciens messages
    setState({ loading:true, result:null, err:null });
    onValidationChange && onValidationChange({ ok:false, errors:[], warnings:[], info:[] });

    async function run(){
      try {
        const res = await validateTemplateAndConfig(templateFile, configFile);
        if (cancelled) return;
        setState({ loading:false, result:res, err:null });
        onValidationChange && onValidationChange(res);
      } catch (e) {
        if (cancelled) return;
        setState({ loading:false, result:null, err:e });
        onValidationChange && onValidationChange({
          ok:false,
          errors:[{ level:"error", code:"exception", msg:e?.message || String(e) }],
          warnings:[],
          info:[]
        });
      }
    }
    const t = setTimeout(run, 150);
    return ()=>{ cancelled=true; clearTimeout(t); };
  }, [templateFile, configFile]);

  const { result:res, err } = state;

  if (!templateFile) return null;

  return (
    <div style={{ marginTop: 12 }}>
      
    </div>
  );
}
